import React, { useCallback, useEffect, useState } from "react"
import { PaySetting, PaySettingConfigured, PaySettings } from "../../services/authinterface"
import { useFormik } from "formik"

import { request } from "../../services/request"
import * as Yup from "yup"
import { Form, Modal } from "react-bootstrap"

import { toast, ToastContainer } from "react-toastify"
import PrimaryButton from "../../components/subComponents/PrimaryButton"
import ToggleSwitch from "../../components/common/ToggleSwitch"
import SecondaryButton from "../../components/subComponents/SecondaryButton"
import { useDispatch } from "react-redux"

import { changeloaderState } from "../../store/loaderSlice"
import { AppDispatch } from "../../store/store"

// Write-only fields. The API never returns these, so an empty input means "keep the
// stored key", never "clear it" - stripped from the payload before submit.
const SECRET_FIELDS = [
  "test_stripe_secret",
  "test_stripe_webhook_secret",
  "stripe_secret",
  "stripe_webhook_secret",
] as const

type SecretField = (typeof SECRET_FIELDS)[number]

const PaymentSetting: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()

  const [paySetting, setPaySetting] = useState<PaySetting | null>(null)
  const [configured, setConfigured] = useState<PaySettingConfigured>({})
  const [status, setStatus] = useState<boolean>(false)
  const [pendingMode, setPendingMode] = useState<boolean | null>(null)
  const [isTogglingMode, setIsTogglingMode] = useState(false)
  const [showInactive, setShowInactive] = useState(false)

  const isLive = status

  const fetchPaySettingDetails = useCallback(async () => {
    try {
      dispatch(changeloaderState(true))

      const response = await request<PaySettings>({
        url: `/adminPaySetting/getPaySettingDetails`,
        method: "GET",
      })

      if (response.data) {
        setPaySetting(response.data)
        setConfigured(response.additionalData ?? {})
        setStatus(response.data?.status === "1")
      } else {
        toast.error("Failed to fetch paySetting: " + response.message)
      }
    } catch (error: any) {
      toast.error(`${error.message}`)
    } finally {
      dispatch(changeloaderState(false))
    }
  }, [dispatch])

  useEffect(() => {
    fetchPaySettingDetails()
  }, [fetchPaySettingDetails])

  // Only the mode actually in use is required. The other mode's settings still submit
  // normally when revealed, but demanding them up front made the page unsavable for
  // anyone running test-only, which is the normal state for a dev environment.
  //
  // Secrets are never required in either mode: the API does not return them, so requiring
  // them would mean retyping all four just to change a bill amount.
  const validationSchema = Yup.object().shape({
    test_stripe_secret: Yup.string().trim(),
    test_stripe_webhook_secret: Yup.string().trim(),
    stripe_secret: Yup.string().trim(),
    stripe_webhook_secret: Yup.string().trim(),

    test_SOS_bill_amount: isLive
      ? Yup.number().typeError("Test SOS bill amount must be a number").min(0)
      : Yup.number()
          .typeError("Test SOS bill amount must be a number")
          .required("Please enter test SOS bill amount")
          .min(0, "Test SOS bill amount must be positive"),
    test_Sensor_bill_amount: isLive
      ? Yup.number().typeError("Test Sensor bill amount must be a number").min(0)
      : Yup.number()
          .typeError("Test Sensor bill amount must be a number")
          .required("Please enter test sensor bill amount")
          .min(0, "Test Sensor bill amount must be positive"),
    test_custom_productId: isLive
      ? Yup.string().trim()
      : Yup.string().trim().required("Please enter test custom product id"),

    SOS_bill_amount: isLive
      ? Yup.number()
          .typeError("SOS bill amount must be a number")
          .required("Please enter SOS bill amount")
          .min(0, "SOS bill amount must be positive")
      : Yup.number().typeError("SOS bill amount must be a number").min(0),
    Sensor_bill_amount: isLive
      ? Yup.number()
          .typeError("Sensor bill amount must be a number")
          .required("Please enter Sensor bill amount")
          .min(0, "Sensor bill amount must be positive")
      : Yup.number().typeError("Sensor bill amount must be a number").min(0),
    custom_productId: isLive
      ? Yup.string().trim().required("Please enter custom product id")
      : Yup.string().trim(),
  })

  const formik = useFormik({
    initialValues: {
      // Always blank. There is nothing to prefill them with.
      test_stripe_secret: "",
      test_stripe_webhook_secret: "",
      stripe_secret: "",
      stripe_webhook_secret: "",

      test_SOS_bill_amount: paySetting?.test_SOS_bill_amount || "",
      test_Sensor_bill_amount: paySetting?.test_Sensor_bill_amount || "",
      SOS_bill_amount: paySetting?.SOS_bill_amount || "",
      Sensor_bill_amount: paySetting?.Sensor_bill_amount || "",
      test_custom_productId: paySetting?.test_custom_productId || "",
      custom_productId: paySetting?.custom_productId || "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      // Send only the secrets that were actually typed. The backend also refuses to write
      // a blank over a stored key, but stripping here keeps the request honest about what
      // it intends to change.
      const payload: Record<string, string> = { ...values }
      for (const field of SECRET_FIELDS) {
        if (!payload[field] || payload[field].trim() === "") {
          delete payload[field]
        }
      }

      try {
        const response = await request<PaySettings>({
          url: `/adminPaySetting/updatePaySetting`,
          method: "POST",
          data: payload,
        })
        if (response.data) {
          toast.success(response.message)
          // Clear the secret inputs so a stale value cannot be resubmitted, and re-read so
          // the "Currently set" markers reflect what was just saved.
          resetForm()
          fetchPaySettingDetails()
        } else {
          toast.error(response.message)
        }
      } catch (error: any) {
        toast.error(`${error?.response?.data?.message ||
        error?.message ||
        "Something went wrong"}`)
      }
    },
  })

  // The API happily switches to a mode with no key on file, and the failure only shows up
  // later as payments breaking, so the warning has to happen here.
  const targetModeReady = pendingMode
    ? configured.has_stripe_secret === true
    : configured.has_test_stripe_secret === true

  const confirmModeChange = async () => {
    if (pendingMode === null) return
    setIsTogglingMode(true)
    try {
      const response = await request<PaySettings>({
        url: `/adminPaySetting/updateStatus`,
        method: "POST",
        data: { status: pendingMode ? "1" : "0" },
      })
      if (response.data) {
        // Only move the switch once the API confirms. It used to flip optimistically, so a
        // rejected call left the UI showing a mode the server was not in.
        setStatus(pendingMode)
        setShowInactive(false)
        toast.success(response.message)
        setPendingMode(null)
        fetchPaySettingDetails()
      } else {
        toast.error(response.message)
      }
    } catch (error: any) {
      toast.error(`${error.message}`)
    } finally {
      setIsTogglingMode(false)
    }
  }

  const secretStatusLabel = (field: SecretField) => {
    const isSet = configured[`has_${field}` as keyof PaySettingConfigured]
    if (isSet === undefined) return null
    return isSet ? (
      <span className='text-xs' style={{ color: "#198754" }}>
        Currently set. Leave blank to keep it.
      </span>
    ) : (
      <span className='text-xs' style={{ color: "#dc3545" }}>
        Not set.
      </span>
    )
  }

  const renderSecretField = (field: SecretField, label: string) => (
    <div className='col-12 col-sm-6'>
      <div className='form-group login-input-section'>
        <label htmlFor={field}>{label}</label>
        <Form.Control
          type='password'
          autoComplete='new-password'
          className='form-control'
          id={field}
          name={field}
          placeholder='Leave blank to keep the current key'
          value={formik.values[field]}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          isInvalid={!!formik.errors[field] && formik.touched[field]}
        />
        <div className='d-flex flex-column'>
          {secretStatusLabel(field)}
          <span className='text-red-500 text-xs'>
            {formik.touched[field] && formik.errors[field]}
          </span>
        </div>
      </div>
    </div>
  )

  const renderTextField = (
    field: keyof typeof formik.values,
    label: string,
    placeholder: string,
    colClass = "col-12 col-sm-6",
  ) => (
    <div className={colClass}>
      <div className='form-group login-input-section'>
        <label htmlFor={field}>{label}</label>
        <Form.Control
          type='text'
          className='form-control'
          id={field}
          name={field}
          placeholder={placeholder}
          value={formik.values[field]}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          isInvalid={!!formik.errors[field] && formik.touched[field]}
        />
        <span className='text-red-500 text-xs'>
          {formik.touched[field] && formik.errors[field]}
        </span>
      </div>
    </div>
  )

  const testFields = (
    <div className='row'>
      {renderSecretField("test_stripe_secret", "Test Stripe Secret")}
      {renderSecretField("test_stripe_webhook_secret", "Test Stripe Webhook Secret")}
      {renderTextField(
        "test_SOS_bill_amount",
        "Test SOS Bill Amount",
        "Enter test SOS bill amount",
        "col-12 col-sm-6 col-lg-3",
      )}
      {renderTextField(
        "test_Sensor_bill_amount",
        "Test Sensor Bill Amount",
        "Enter test sensor bill amount",
        "col-12 col-sm-6 col-lg-3",
      )}
      {renderTextField(
        "test_custom_productId",
        "Test Custom Product Id",
        "Enter test custom product id",
      )}
    </div>
  )

  const liveFields = (
    <div className='row'>
      {renderSecretField("stripe_secret", "Live Stripe Secret")}
      {renderSecretField("stripe_webhook_secret", "Live Stripe Webhook Secret")}
      {renderTextField(
        "SOS_bill_amount",
        "Live SOS Bill Amount",
        "Enter SOS bill amount",
        "col-12 col-sm-6 col-lg-3",
      )}
      {renderTextField(
        "Sensor_bill_amount",
        "Live Sensor Bill Amount",
        "Enter sensor bill amount",
        "col-12 col-sm-6 col-lg-3",
      )}
      {renderTextField("custom_productId", "Live Custom Product Id", "Enter custom product id")}
    </div>
  )

  const activeLabel = isLive ? "Live" : "Test"
  const inactiveLabel = isLive ? "Test" : "Live"

  return (
    <section className='dashboard-section'>
      <ToastContainer position='top-right' autoClose={2000} theme='light' />

      <Modal
        className='comman-modal'
        show={pendingMode !== null}
        onHide={() => setPendingMode(null)}
        centered
      >
        <Modal.Header closeButton className='comman-modal-header'>
          <Modal.Title className='comman-modal-title'>
            Switch to {pendingMode ? "Live" : "Test"} mode?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className='comman-modal-body'>
          {pendingMode ? (
            <p>
              Live mode charges real customer cards using the live Stripe keys. Make sure the live
              secret and webhook secret are correct before switching.
            </p>
          ) : (
            <p>
              Test mode routes all payments to the test Stripe keys. If this environment expects
              live mode, the backend will refuse to process payments at all and will raise an alert
              until this is switched back.
            </p>
          )}
          {!targetModeReady && (
            <p
              className='p-2'
              style={{ background: "#fdecea", borderLeft: "4px solid #dc3545", borderRadius: 4 }}
            >
              <strong>
                There is no {pendingMode ? "live" : "test"} Stripe secret on file yet.
              </strong>{" "}
              Switching now leaves this environment unable to process payments. Enter the{" "}
              {pendingMode ? "live" : "test"} key and save first.
            </p>
          )}
          <div className='d-flex gap-3 justify-content-center mt-3'>
            <SecondaryButton
              type='button'
              label='Cancel'
              className='editFormButton'
              onClick={() => setPendingMode(null)}
              disabled={isTogglingMode}
            />
            <PrimaryButton
              type='button'
              label={isTogglingMode ? "Switching..." : "Yes, switch"}
              className='editFormButton'
              onClick={confirmModeChange}
              disabled={isTogglingMode}
            />
          </div>
        </Modal.Body>
      </Modal>

      <div className='container'>
        <div className='row justify-content-center'>
          <div className='col-12'>
            <div className='dashboard-table-title-section title-line'>
              <h2>Payment Settings</h2>
              <ToggleSwitch
                isOn={status}
                handleToggle={() => setPendingMode(!status)}
                onLabel='Live Mode'
                offLabel='Test Mode'
              />
            </div>
          </div>
          <div className='col-xl-8 col-lg-10 col-md-10 col-12 mt-4 w-100'>
            <form onSubmit={formik.handleSubmit} className='customer-edit-form'>
              <h4 className='owner-details-title'>{activeLabel} mode settings (in use)</h4>
              <p className='text-xs'>
                These are the values this environment is currently running on.
              </p>
              {isLive ? liveFields : testFields}

              <div className='mt-4'>
                <button
                  type='button'
                  className='primaryButton com-button table-btn-1'
                  onClick={() => setShowInactive((prev) => !prev)}
                >
                  {showInactive ? "Hide" : "Show"} {inactiveLabel.toLowerCase()} mode settings
                </button>
                {/* Collapsed rather than removed: the other mode has to be configurable
                    before switching into it, and updateStatus does no checking of its own,
                    so hiding it outright would mean switching to an unconfigured mode and
                    breaking payments just to reach the fields. */}
                <p className='text-xs mt-1'>
                  Not in use right now. Fill these in before switching to {inactiveLabel.toLowerCase()} mode.
                </p>
                {showInactive && <div className='mt-3'>{isLive ? testFields : liveFields}</div>}
              </div>

              <div className='row mt-4'>
                <div className='col-12'>
                  <div className='d-flex gap-3 justify-content-center'>
                    <div className='d-flex gap-3'>
                      <PrimaryButton
                        type='submit'
                        label={formik.isSubmitting ? "Saving..." : "Submit"}
                        className='editFormButton w-100'
                        disabled={formik.isSubmitting}
                      />
                      <SecondaryButton
                        type='button'
                        onClick={() => {
                          formik.resetForm()
                          fetchPaySettingDetails()
                        }}
                        label='Reset'
                        className='editFormButton w-100'
                      />
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  )
}

export default PaymentSetting
